import { useEffect, useState } from "react";
import { getHomepageBannerService } from "./homepage-banner-service";
import { HomepageBannerDataProps } from "./types";
import HomepageBannerSlides from "./homepageBannerSlides";
import AsSeenOnLogos from "./asSeenOnLogos";
import { HeadingAndDescription } from "./homepageCompetitions/headingAndDescription";

// Cache key and TTL for the stale-while-revalidate localStorage strategy.
// Storing the previous banner data means returning users see the banner instantly
// without any API wait, eliminating the black-rectangle flash on repeat visits.
const BANNER_CACHE_KEY = "botb_banner_v1";
const BANNER_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

const readBannerCache = (): HomepageBannerDataProps | null => {
  try {
    const raw = localStorage.getItem(BANNER_CACHE_KEY);
    if (!raw) return null;
    const { data, cachedAt } = JSON.parse(raw);
    if (Date.now() - cachedAt > BANNER_CACHE_TTL_MS) return null;
    return data as HomepageBannerDataProps;
  } catch {
    return null;
  }
};

const writeBannerCache = (data: HomepageBannerDataProps) => {
  try {
    localStorage.setItem(BANNER_CACHE_KEY, JSON.stringify({ data, cachedAt: Date.now() }));
  } catch {
    // localStorage may be unavailable (private browsing, storage quota) — silently ignore
  }
};

const HomepageBanner = () => {
  // Initialise state from localStorage so returning users see the banner instantly
  const [homepageBannerData, setHomepageBannerData] = useState<HomepageBannerDataProps | undefined>(
    () => readBannerCache() ?? undefined
  );

  useEffect(() => {
    getHomepageBanner();
  }, []);

  const getHomepageBanner = async () => {
    try {
      const response = await getHomepageBannerService();
      const jsonResponse = await response.json();
      const freshData: HomepageBannerDataProps = { data: jsonResponse.HomepageBanner };
      // Always update state with fresh data and persist to cache for the next visit
      setHomepageBannerData(freshData);
      writeBannerCache(freshData);
    } catch (err) {
      console.log(`Error while fetching the homepage banner data - ${JSON.stringify(err)}`);
    }
  };

  return (
    <div className={`bg-black`}>
      <div className={"relative z-[1]"}>
        {homepageBannerData ? (
          <HomepageBannerSlides
            mediaList={homepageBannerData.data.BannerList}
            slideAutoPlay={homepageBannerData.data.BannerSlideAutoPlay}
            slideDelay={homepageBannerData.data.BannerSlideDelay}
          />
        ) : (
          <div className={"aspect-square lg:aspect-[2496/787]"} />
        )}
        <HeadingAndDescription />
      </div>
      <AsSeenOnLogos />
    </div>
  );
};

export default HomepageBanner;
